import { signOut } from "@aws-amplify/auth";
import { getAuthHeaders } from "../AuthHeader/getAuthHeader";
import api from "../Interceptors";
import toast from "react-hot-toast";
import { ApiError } from "../ApiError";
import { clearAllAuthStorage } from "../../utils/authStorage";
import { GetAllProjectsParams } from "../Projects";
import { AxiosError } from "axios";

export const getAllUsers = async (page: number = 1, limit: number = 10, search?: string) => {
    try {
        const authHeaders = await getAuthHeaders();
        const params: GetAllProjectsParams = {
            page,
            limit,
        };
        const trimmed = (search ?? "").trim();
        if (trimmed) {
            params.search = trimmed.substring(0, 256); // Cap length; backend should also enforce query limits
        }
        const config = {
            headers: {
                "Content-Type": "application/json",
                ...authHeaders,
            },
            params,
        };
        const { data } = await api.get("/users", config);
        return data;
    } catch (error) {
        throw new ApiError(error);
    }
}

export const fetchUserById = async (id: string) => {
    try {
        const authHeaders = await getAuthHeaders();
        const config = {
            headers: {
                "Content-Type": "application/json",
                ...authHeaders,
            },
        };
        const { data } = await api.get(`/users/${id}`, config);
        const raw = data?.user ?? data;
        if (!raw || typeof raw !== "object") return null;
        return {
            id: raw.id ?? raw.userId ?? id,
            fullName: raw.fullName ?? ([raw.firstName, raw.lastName].filter(Boolean).join(" ") || ""),
            email: raw.email ?? "",
            firstName: raw.firstName ?? "",
            lastName: raw.lastName ?? "",
            status: raw.status ?? "",
            cognito_sub: raw.cognito_sub ?? null,
            profileImage: raw.profileImage ?? raw.profileImageUrl ?? null,
            profileImagePreview: raw.profileImagePreview ?? raw.profileImage ?? raw.profileImageUrl ?? null,
            type: raw.type ?? "",
            createdAt: raw.createdAt ?? "",
            updatedAt: raw.updatedAt ?? "",
            companyName: raw.companyName ?? null,
            jobTitle: raw.jobTitle ?? null,
            phone: raw.phone ?? null,
        };
    } catch (error) {
        throw new ApiError(error);
    }
};

export const updateUser = async (userId: string, userData: { fullName?: string; email?: string; type?: string; firstName?: string; lastName?: string; company?: string; companyName?: string; jobTitle?: string; phone?: string; profileImage?: string }) => {
    try {
        const authHeaders = await getAuthHeaders();
        const config = {
            headers: {
                "Content-Type": "application/json",
                ...authHeaders,
            },
        };
        const { data } = await api.put(`/users/${userId}`, userData, config);
        return data;
    } catch (error) {
        throw new ApiError(error);
    }
}

export const deleteLocalUser = async (userId: string) => {
    try {
        const authHeaders = await getAuthHeaders();
        const config = {
            headers: {
                "Content-Type": "application/json",
                ...authHeaders,
            },
        };
        const { data } = await api.delete(`/users/${userId}`, config);
        return data;
    } catch (error) {
        throw new ApiError(error);
    }
}

export const getCurrentUserDetails = async () => {
    try {
        const authHeaders = await getAuthHeaders();
        const config = {
            headers: {
                "Content-Type": "application/json",
                ...authHeaders,
            },
        };
        const { data } = await api.get("/get/me", config);
        // Support both { user: {...} } and { ...userFields } response shapes
        const user = data?.user ?? data;
        return user && typeof user === "object" ? user : null;
    } catch (error: unknown) {
        if (error instanceof AxiosError) {
            if (error?.response?.status === 404) {
                toast.error("User not found. Please log in again.");
                signOut();
                localStorage.clear();
                window.location.href = "/login";
            }
            throw new ApiError(error);
        }
        throw new ApiError(error);
    }
}


export interface CreateUserPayload {
    email?: string;
    [key: string]: unknown;
}

export const createUser = async (payload: CreateUserPayload) => {
    try {
        const authHeaders = await getAuthHeaders()
        const normalizedEmail =
            typeof payload.email === "string" ? payload.email.trim().toLowerCase() : undefined;
        if (normalizedEmail !== undefined) {
            payload.email = normalizedEmail;
        }
        const normalizedPayload = {
            ...payload,
            ...(normalizedEmail !== undefined ? { email: normalizedEmail } : {}),
        };
        const config = {
            headers: {
                "Content-Type": "application/json",
                ...authHeaders,
            },
            // UI already handles the error toast for create-user forms; avoid duplicate toasts from interceptor.
            suppressToast: true,
        }
        const { data } = await api.post("/auth/create-user", normalizedPayload, config)
        return data
    } catch (error) {
        throw new ApiError(error);
    }
}

export const logoutUser = async () => {
    try {
        clearAllAuthStorage();
        await signOut();
        window.location.href = "/login";
    } catch (error) {
        throw new ApiError(error);
    }
}
